import { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Heart, Star, Truck, Shield, Minus, Plus, Loader2, Share2, ChevronRight, MapPin, Store, Check, Home, Building, User } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { Layout } from '@/components/layout/Layout';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { useProduct } from '@/hooks/useApi.js';
import { useCart } from '@/context/CartContext';
import { useWishlist } from '@/context/WishlistContext';
import { useAuth } from '@/context/AuthContext';
import { useReview } from '@/context/ReviewContext';
import { useAddress, type Address } from '@/context/AddressContext';
import { publicFetch } from '@/services/api';
import ReviewFormModal from '@/components/ReviewFormModal';
import DeliveryTimeline from '@/components/DeliveryTimeline';
import { getSizeChartImage } from '@/utils/sizeChartResolver';
import { cn } from '@/lib/utils';
import { cache, CACHE_KEYS, CACHE_TTL } from '@/utils/cache';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";

interface ProductImage {
  image_url: string;
  display_order?: number;
  color?: string;
}
// Updated types to match your real backend response
interface ProductVariant {
  variantId: number;
  size: {
    code: string;
    name: string;
  };
  sku: string;
  originalPrice: number;
  discount: number;
  offerPrice: number;
  stockQuantity: number;
  images: ProductImage[];   // ✅ FIX
  color?: string;
}

interface Product {
  productId: number;
  productName: string;
  productDescription: string;
  rating: number;
  categoryName: string;
  subCategoryName: string;
  variants: ProductVariant[];
  details: {
    brand: string;
    fabric?: string;
    fit?: string;
    occasion?: string;
  };
  images: ProductImage[];
  seller?: {
    id: number;
    sellerId?: number;
    shopName: string;
    sellerName?: string;
    city: string;
    state: string;
    addressLine?: string;
    pincode?: string;
  };
}

const ProductDetailPage = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { addToCart, cart } = useCart();
  const { isInWishlist, toggleWishlist } = useWishlist();
  const { user, setShowSignInModal } = useAuth();
  const { defaultAddress, addresses, setDefaultAddress } = useAddress();

  // Address selection states
  const [selectedAddress, setSelectedAddressState] = useState<Address | null>(null);
  const [isAddressExpanded, setIsAddressExpanded] = useState(false);

  const productId = Number(id);
  const { data: product, isLoading, error, isFetching, isError } =
    useProduct(Number.isNaN(productId) ? undefined : productId);

  // Review context
  const {
    reviews,
    reviewSummary,
    loading: reviewLoading,
    userHasReviewed,
    fetchProductReviews,
    fetchReviewSummary,
    checkUserReviewed,
  } = useReview();

  const [selectedVariant, setSelectedVariant] = useState<ProductVariant | null>(null);
  const [quantity, setQuantity] = useState(1);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [showReviewForm, setShowReviewForm] = useState(false);
  const [similarProducts, setSimilarProducts] = useState<Product[]>([]);
  const [selectedReviewImage, setSelectedReviewImage] = useState<string | null>(null);

  // New state for missing features
  const [showSizeChart, setShowSizeChart] = useState(false);
  const [scrollY, setScrollY] = useState(0);
  const [showStickyBar, setShowStickyBar] = useState(false);
  const [colorsForProduct, setColorsForProduct] = useState<string[]>([]);
  const [selectedColor, setSelectedColor] = useState<string | null>(null);
  const [variantsForSelectedColor, setVariantsForSelectedColor] = useState<ProductVariant[]>([]);

  // Refs for scroll tracking
  const priceRef = useRef<HTMLDivElement>(null);
  const actionButtonsRef = useRef<HTMLDivElement>(null);

  // Fetch similar products with caching
  const fetchSimilarProducts = useCallback(async (productId: number) => {
    try {
      // Check cache first
      const cached = cache.get(
        CACHE_KEYS.SIMILAR_PRODUCTS,
        { productId },
        { ttl: CACHE_TTL.PRODUCT_STATIC }
      );
      
      if (cached) {
        console.log('🟢 Cache HIT: Similar products');
        setSimilarProducts(Array.isArray(cached) ? cached.slice(0, 8) : []);
        return;
      }

      console.log('🔴 Cache MISS: Fetching similar products from API');
      const response = await publicFetch(`/api/products/related/${productId}`);
      if (response.ok) {
        const data = await response.json();
        const products = Array.isArray(data) ? data.slice(0, 8) : [];
        
        // Cache the result
        cache.set(
          CACHE_KEYS.SIMILAR_PRODUCTS,
          data,
          { productId },
          { ttl: CACHE_TTL.PRODUCT_STATIC }
        );
        
        setSimilarProducts(products);
      } else {
        setSimilarProducts([]);
      }
    } catch (error) {
      console.error('Error fetching similar products:', error);
      setSimilarProducts([]);
    }
  }, []); // Empty dependency array since it doesn't depend on any props or state

  // Initialize colors and variants
  const initColorVariantState = useCallback((baseProduct: Product) => {
    if (!baseProduct) return;
    const variants = Array.isArray(baseProduct.variants) ? baseProduct.variants : [];

    if (!variants.length) {
      setColorsForProduct([]);
      setSelectedColor(null);
      setVariantsForSelectedColor([]);
      setSelectedVariant(null);
      return;
    }

    let colorSet = new Set(
      variants
        .map((v) => (v.color || '').trim())
        .filter((c) => c && c.toLowerCase() !== 'null')
    );

    const colors = Array.from(colorSet);
    setColorsForProduct(colors);

    const initialColor = colors[0] || null;
    setSelectedColor(initialColor || null);

    const variantsForColor = initialColor
      ? variants.filter((v) => (v.color || '').trim() === initialColor)
      : variants;

    setVariantsForSelectedColor(variantsForColor);

    const defaultVariant =
      variantsForColor.find((v) => (v.stockQuantity ?? 0) > 0) ||
      variantsForColor[0] ||
      variants[0] ||
      null;

    setSelectedVariant(defaultVariant);
  }, []); // Empty dependency since it only uses setState functions

  // Auto-select first variant when product loads
  useEffect(() => {
    if (product?.variants?.length > 0) {
      initColorVariantState(product);
    }
  }, [product, initColorVariantState]);

  // Update variants when color changes
  useEffect(() => {
    if (!product || !Array.isArray(product.variants) || !product.variants.length) return;

    const variants = product.variants;
    let filtered = selectedColor && selectedColor.trim()
      ? variants.filter((v) => (v.color || '').trim() === selectedColor.trim())
      : variants;

    if (!filtered.length) {
      filtered = variants;
    }

    setVariantsForSelectedColor(filtered);

    const available = filtered.filter((v) => (v.stockQuantity ?? 0) > 0);
    const pool = available.length ? available : filtered;

    const stillValid = selectedVariant &&
      pool.some((v) => v.variantId === selectedVariant.variantId);

    if (!stillValid) {
      const newVariant = pool.find((v) => (v.stockQuantity ?? 0) > 0) || pool[0] || null;
      setSelectedVariant(newVariant);
    }

    // Reset image index when color changes to show color-specific images
    setCurrentImageIndex(0);
  }, [selectedColor, product]); // Removed selectedVariant to prevent circular dependency

  // Scroll tracking for sticky bar
  useEffect(() => {
    const handleScroll = () => {
      const currentScrollY = window.scrollY;
      setScrollY(currentScrollY);

      // Show sticky bar when scrolled past the price section
      if (priceRef.current) {
        const priceRect = priceRef.current.getBoundingClientRect();
        setShowStickyBar(priceRect.bottom < 0);
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Fetch reviews when product loads
  useEffect(() => {
    if (product?.productId) {
      // Only fetch if reviews haven't been loaded for this product yet
      const timeoutId = setTimeout(() => {
        fetchProductReviews(product.productId);
        fetchReviewSummary(product.productId);
        // Only check user review status if user is authenticated
        if (user?.isAuthenticated) {
          checkUserReviewed(product.productId);
        }
      }, 100);

      return () => clearTimeout(timeoutId);
    }
  }, [product?.productId, user?.isAuthenticated]); // Functions are stable callbacks, don't include them

  // Fetch similar products when product loads
  useEffect(() => {
    if (product?.productId) {
      const timeoutId = setTimeout(() => {
        fetchSimilarProducts(product.productId);
      }, 200); // Slightly delayed to avoid overlapping requests

      return () => clearTimeout(timeoutId);
    }
  }, [product?.productId, fetchSimilarProducts]);

  // Update selected address when default changes
  useEffect(() => {
    setSelectedAddressState(defaultAddress);
  }, [defaultAddress]);



  const formatPrice = (price: number) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 0,
    }).format(price);
  };



  // Address helper functions
  const handleSelectAddress = async (address: Address) => {
    try {
      await setDefaultAddress(address.addressId);
      setSelectedAddressState(address);
      setIsAddressExpanded(false);
    } catch (error) {
      console.error('Failed to update address:', error);
    }
  };

  const getAddressTypeIcon = (type: string) => {
    switch (type) {
      case 'HOME': return Home;
      case 'WORK': return Building;
      case 'OTHER': default: return User;
    }
  };

  const getAddressTypeColor = (type: string) => {
    switch (type) {
      case 'HOME': return 'text-green-600';
      case 'WORK': return 'text-blue-600';
      case 'OTHER': default: return 'text-gray-600';
    }
  };

  // Size chart helper
  const sizeChartImage = getSizeChartImage(product?.categoryName, product?.subCategoryName);

  // Loading state
  if (isLoading) {
    return (
      <Layout>
        <div className="min-h-screen flex flex-col">
          <div className="flex-1 container mx-auto px-4 py-20 text-center">
            <Loader2 className="h-12 w-12 animate-spin mx-auto mb-4" />
            <p className="text-lg text-muted-foreground">Loading product details...</p>
          </div>
        </div>
      </Layout>
    );
  }

  // Error state
  if (error) {
    return (
      <Layout>
        <div className="container mx-auto px-4 py-20 text-center">
          <h1 className="text-2xl font-bold mb-4">Error loading product</h1>
          <p className="text-muted-foreground mb-4">{error.message || "Something went wrong"}</p>
          <Button onClick={() => navigate('/')}>Go Back Home</Button>
        </div>
      </Layout>
    );
  }

  // Product not found
  if (!product) {
    return (
      <Layout>
        <div className="container mx-auto px-4 py-20 text-center">
          <h1 className="text-2xl font-bold mb-4">Product not found</h1>
          <Button onClick={() => navigate('/')}>Go Back Home</Button>
        </div>
      </Layout>
    );
  }

const inWishlist = isInWishlist(product.productId);

  // Determine images to display - extract image_url from image objects and sort by display_order
  const images = (() => {
    // Helper function to extract URLs from image objects and sort by display_order
    const extractAndSortImageUrls = (imageArray: ProductImage[]): string[] => {
  if (!Array.isArray(imageArray)) return [];

  return imageArray
    .filter(
      (img): img is ProductImage =>
        !!img && typeof img === 'object' && !!img.image_url
    )
    .sort(
      (a, b) => (a.display_order ?? 0) - (b.display_order ?? 0)
    )
    .map(img => img.image_url);
};

    // First try to filter by selected color if available
    if (selectedColor && selectedColor.trim() && product.images?.length > 0) {
      const colorImages = product.images.filter(
        (img: any) => (img.color || '').trim() === selectedColor.trim()
      );
      if (colorImages.length > 0) {
        const urls = extractAndSortImageUrls(colorImages);
        if (urls.length > 0) return urls;
      }
    }

    // Try selected variant images first
    if (selectedVariant?.images?.length > 0) {
      const urls = extractAndSortImageUrls(selectedVariant.images);
      if (urls.length > 0) return urls;
    }

    // Try product images
    if (product.images?.length > 0) {
      const urls = extractAndSortImageUrls(product.images);
      if (urls.length > 0) return urls;
    }

    // Try any variant images
    const anyVariantWithImages = product.variants?.find(v => v.images?.length > 0);
    if (anyVariantWithImages) {
      const urls = extractAndSortImageUrls(anyVariantWithImages.images);
      if (urls.length > 0) return urls;
    }

    // Fallback to placeholder
    return ['data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNDAwIiBoZWlnaHQ9IjQwMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iMTAwJSIgaGVpZ2h0PSIxMDAlIiBmaWxsPSIjZjNmNGY2Ci8+PHRleHQgeD0iNTAlIiB5PSI1MCUiIGZvbnQtZmFtaWx5PSJBcmlhbCIgZm9udC1zaXplPSIxOCIgZmlsbD0iIzlmYTZiNyIgdGV4dC1hbmNob3I9Im1pZGRsZSIgZHk9IjAuM2VtIj5ObyBJbWFnZTwvdGV4dD48L3N2Zz4='];
  })();

  // Use first variant for price display if none selected
  const displayVariant = selectedVariant;

  // Check if item is in cart
  const isInCart = (variantId: number) => {
    return cart.some(item => item.variantId === variantId);
  };

  const handleAddToCart = async () => {
    // ✅ 1. Enforce size selection
    if (!selectedVariant) {
      alert('Please select a size');
      return;
    }

    // ✅ 2. Use selectedVariant directly (not displayVariant)
    if (selectedVariant.stockQuantity <= 0) {
      alert('This item is out of stock');
      return;
    }

    // ✅ 3. Check if already in cart
    if (isInCart(selectedVariant.variantId)) {
      window.location.href = '/cart';
      return;
    }

    try {
      const cartItem = {
        variantId: selectedVariant.variantId,
        productId: product.productId,
        productName: product.productName,
        quantity,
        salePrice: Number(selectedVariant.offerPrice || selectedVariant.originalPrice || 0),
        originalPrice: selectedVariant.originalPrice,
        offerPrice: selectedVariant.offerPrice,
        size: selectedVariant.size.code,
        sizeName: selectedVariant.size.name,
        color: selectedVariant.color || selectedColor || 'Default',
        sku: selectedVariant.sku,
        stockQuantity: selectedVariant.stockQuantity,
        images: images.map((img, index) => ({
          id: index + 1,
          imageUrl: img,
          imageType: 'OTHER',
          displayOrder: index + 1,
        })),
        productCategoryName: product.categoryName,
        productSubCategoryName: product.subCategoryName,
        seller: {
          id: product.seller?.id || null,
          shopName: product.seller?.shopName || 'AuraKart',
          city: product.seller?.city || 'Chennai',
          state: product.seller?.state || 'Tamil Nadu',
        },
      };

      await addToCart(cartItem);
      alert(`${product.productName} added to cart!`);
    } catch (error) {
      alert('Failed to add to cart. Please try again.');
    }
  };

 const handleWishlistToggle = async () => {
  if (!user?.isAuthenticated) {
    setShowSignInModal(true);
    return;
  }

  await toggleWishlist(product.productId);
};

  const handleBuyNow = async () => {
    await handleAddToCart();
    navigate('/cart');
  };

  // Helper to render address summary
  const AddressSummary = () => {
    const currentAddress = selectedAddress || defaultAddress;
    
    if (!currentAddress) {
      return (
        <div className="flex items-center gap-3 p-3 border rounded-lg bg-amber-50 border-amber-200">
          <MapPin className="h-4 w-4 text-amber-600" />
          <div className="flex-1">
            <p className="font-medium text-amber-800">Add delivery address</p>
            <p className="text-sm text-amber-600">Add an address to see exact delivery estimates</p>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={() => navigate('/address-management')}
          >
            Add
          </Button>
        </div>
      );
    }

    const shortLine = `${currentAddress.addressLine1 || ''}${currentAddress.addressLine2 ? ', ' + currentAddress.addressLine2 : ''}`;
    return (
      <div className="space-y-3">
        <div className="p-3 border rounded-lg bg-green-50 border-green-200">
          <div className="flex justify-between items-start">
            <div className="flex items-start gap-3 flex-1">
              <MapPin className="h-4 w-4 text-green-600 mt-0.5" />
              <div className="flex-1">
                <p className="font-medium text-green-800">{currentAddress.fullName}</p>
                <p className="text-sm text-green-600 line-clamp-1">
                  {shortLine} • {currentAddress.city}
                </p>
              </div>
            </div>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setIsAddressExpanded(!isAddressExpanded)}
              className="text-green-700 hover:text-green-800"
            >
              Change
            </Button>
          </div>
        </div>

        {/* Address Selection Modal */}
        <AnimatePresence>
          {isAddressExpanded && (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 10 }}
              className="p-4 bg-secondary/50 rounded-lg border"
            >
              <div className="flex items-center justify-between mb-3">
                <h4 className="font-medium">Select Delivery Address</h4>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setIsAddressExpanded(false)}
                >
                  Cancel
                </Button>
              </div>

              <div className="space-y-3 max-h-60 overflow-y-auto">
                {addresses.map((address) => {
                  const AddressIcon = getAddressTypeIcon(address.addressType);
                  const iconColor = getAddressTypeColor(address.addressType);

                  return (
                    <div
                      key={address.addressId}
                      className={`p-3 rounded-lg border-2 cursor-pointer transition-all ${
                        currentAddress?.addressId === address.addressId
                          ? 'border-primary bg-primary/5'
                          : 'border-border hover:border-primary/50 bg-background'
                      }`}
                      onClick={() => handleSelectAddress(address)}
                    >
                      <div className="flex items-start gap-3">
                        <div className={`w-8 h-8 rounded-full bg-secondary flex items-center justify-center ${iconColor}`}>
                          <AddressIcon className="h-4 w-4" />
                        </div>
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <span className="font-medium text-sm">{address.addressType}</span>
                            {address.isDefault && (
                              <span className="bg-primary text-white text-xs px-2 py-0.5 rounded-full">
                                Default
                              </span>
                            )}
                          </div>
                          <p className="font-medium text-sm">{address.fullName}</p>
                          <p className="text-xs text-muted-foreground">
                            {address.addressLine1}
                            {address.addressLine2 && `, ${address.addressLine2}`}
                          </p>
                          <p className="text-xs text-muted-foreground">
                            {address.city}, {address.state} - {address.postalCode}
                          </p>
                        </div>
                        {currentAddress?.addressId === address.addressId && (
                          <div className="w-5 h-5 bg-primary rounded-full flex items-center justify-center">
                            <Check className="h-3 w-3 text-white" />
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    );
  };

  return (
    <Layout>
      <div className="container mx-auto px-4 py-6">
        {/* Back Button */}
        <Button variant="ghost" className="mb-6" onClick={() => navigate(-1)}>
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back
        </Button>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          {/* Image Gallery */}
          <div className="space-y-4">
            <motion.div
              className="aspect-square bg-gray-50 rounded-2xl overflow-hidden"
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.3 }}
            >
              <img
                src={images[currentImageIndex]}
                alt={product.productName}
                className="w-full h-full object-cover"
                onError={(e) => {
                  const target = e.currentTarget;
                  if (!target.src.includes('data:image')) {
                    target.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNDAwIiBoZWlnaHQ9IjQwMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iMTAwJSIgaGVpZ2h0PSIxMDAlIiBmaWxsPSIjZjNmNGY2Ii8+PHRleHQgeD0iNTAlIiB5PSI1MCUiIGZvbnQtZmFtaWx5PSJBcmlhbCIgZm9udC1zaXplPSIxOCIgZmlsbD0iIzlmYTZiNyIgdGV4dC1hbmNob3I9Im1pZGRsZSIgZHk9IjAuM2VtIj5ObyBJbWFnZTwvdGV4dD48L3N2Zz4=';
                  }
                }}
              />
            </motion.div>

            {/* Thumbnails */}
            {images.length > 1 && (
              <div className="flex gap-2 overflow-x-auto pb-2">
                {images.map((image, index) => (
                  <button
                    key={index}
                    onClick={() => setCurrentImageIndex(index)}
                    className={cn(
                      "flex-shrink-0 w-20 h-20 rounded-lg overflow-hidden border-2",
                      currentImageIndex === index ? "border-primary" : "border-gray-200"
                    )}
                  >
                    <img
                      src={image}
                      alt={`View ${index + 1}`}
                      className="w-full h-full object-cover"
                    />
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Product Details */}
          <div className="space-y-6">
            {/* Brand & Title */}
            <div className="space-y-1">
              {product.details?.brand && (
                <p className="text-sm text-muted-foreground uppercase tracking-wider">
                  {product.details.brand}
                </p>
              )}
              <h1 className="text-3xl font-bold">{product.productName}</h1>
              <p className="text-muted-foreground">
                {product.categoryName} • {product.subCategoryName}
              </p>
            </div>

            {/* Rating */}
            <div className="cursor-pointer" onClick={() => {
              document.getElementById('customer-reviews')?.scrollIntoView({ behavior: 'smooth' });
            }}>
              <div className="flex items-center gap-3 hover:bg-gray-50 p-2 rounded-lg transition-colors">
                <div className="flex items-center gap-1">
                  <Star className="h-5 w-5 fill-yellow-400 text-yellow-400" />
                  <span className="font-semibold">
                    {reviewSummary?.averageRating ? reviewSummary.averageRating.toFixed(1) : (product.rating || 0).toFixed(1)}
                  </span>
                </div>
                <span className="text-sm text-muted-foreground">
                  ({reviewSummary?.totalReviews || 0} reviews)
                </span>
                <ChevronRight className="h-4 w-4 text-muted-foreground" />
              </div>
            </div>

            {/* Price */}
            {displayVariant && (
              <div ref={priceRef} className="space-y-2">
                <div className="flex items-center gap-3">
                  <span className="text-3xl font-bold">
                    {formatPrice(displayVariant.offerPrice)}
                  </span>
                  {displayVariant.originalPrice > displayVariant.offerPrice && (
                    <>
                      <span className="text-xl text-muted-foreground line-through">
                        {formatPrice(displayVariant.originalPrice)}
                      </span>
                      <Badge className="bg-red-500 hover:bg-red-600">
                        {Math.round(displayVariant.discount)}% OFF
                      </Badge>
                    </>
                  )}
                </div>
                <p className="text-sm text-green-600 font-medium">Inclusive of all taxes</p>
              </div>
            )}

            {/* Color Selection */}
            {colorsForProduct.length > 0 && (
              <div className="space-y-3">
                <h3 className="font-semibold">Select Color</h3>
                <div className="flex flex-wrap gap-3">
                  {colorsForProduct.map((color) => (
                    <button
                      key={color}
                      onClick={() => {
                        setSelectedColor(color);
                      }}
                      className={cn(
                        "px-4 py-2 border rounded-lg text-sm font-medium transition-colors",
                        selectedColor === color
                          ? "border-primary bg-primary/10 text-primary"
                          : "border-gray-300 hover:border-gray-400"
                      )}
                    >
                      {color}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Size Selection */}
            {(() => {
              const availableVariants = variantsForSelectedColor.length > 0 ? variantsForSelectedColor : (product.variants || []);

              return availableVariants.length > 0 && (
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <h3 className="font-semibold">Select Size</h3>
                    {sizeChartImage && (
                      <Button
                        variant="link"
                        size="sm"
                        onClick={() => {
                          setShowSizeChart(true);
                        }}
                        className="text-primary h-auto p-0"
                      >
                        Size Chart
                      </Button>
                    )}
                  </div>
                  <div className="grid grid-cols-3 sm:grid-cols-4 gap-3">
                    {availableVariants.map((variant) => {
                      const isSelected = selectedVariant?.variantId === variant.variantId;
                      const isOutOfStock = (variant.stockQuantity || 0) === 0;
                      const isLowStock = (variant.stockQuantity || 0) <= 5 && (variant.stockQuantity || 0) > 0;

                      return (
                        <Button
                          key={variant.variantId}
                          variant={isSelected ? "default" : "outline"}
                          size="lg"
                          onClick={(e) => {
                            e.preventDefault();
                            if (!isOutOfStock) {
                              setSelectedVariant(variant);
                            }
                          }}
                          disabled={isOutOfStock}
                          className={cn(
                            "h-16 border transition-all duration-200 flex-col",
                            isSelected
                              ? "border-primary bg-primary text-primary-foreground"
                              : "border-gray-300 hover:border-primary hover:bg-primary/5",
                            isOutOfStock && "opacity-50 cursor-not-allowed"
                          )}
                        >
                          <div className="font-medium text-sm">{variant.size.code}</div>
                          {isLowStock && !isOutOfStock && (
                            <div className={cn(
                              "text-xs mt-1 font-medium",
                              isSelected ? "text-orange-200" : "text-orange-600"
                            )}>
                              Only {variant.stockQuantity} left
                            </div>
                          )}
                          {isOutOfStock && (
                            <div className={cn(
                              "text-xs mt-1 font-medium",
                              isSelected ? "text-red-200" : "text-red-600"
                            )}>
                              Out of stock
                            </div>
                          )}
                        </Button>
                      );
                    })}
                  </div>
                </div>
              );
            })()}

            {/* Quantity */}
            <div className="flex items-center gap-4">
              <span className="font-semibold">Quantity:</span>
              <div className="flex items-center border rounded-lg">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setQuantity(Math.max(1, quantity - 1))}
                  disabled={quantity <= 1}
                >
                  <Minus className="h-4 w-4" />
                </Button>
                <span className="w-16 text-center font-medium">{quantity}</span>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setQuantity(quantity + 1)}
                  disabled={displayVariant && quantity >= displayVariant.stockQuantity}
                >
                  <Plus className="h-4 w-4" />
                </Button>
              </div>
              {displayVariant && (
                <span className="text-sm text-muted-foreground">
                  {displayVariant.stockQuantity} available
                </span>
              )}
            </div>

            {/* Sold By Section */}
            <Card className="border border-gray-200">
              <CardContent className="p-4">
                {(() => {
                  // Enhanced seller detection with multiple fallback strategies
                  const seller = product?.seller;
                  const productData = product as any;

                  const sellerId = seller?.id || seller?.sellerId || productData?.sellerId;
                  const sellerName = seller?.shopName || seller?.sellerName || seller?.displayName ||
                    productData?.sellerName || productData?.shopName ||
                    product?.details?.brand || 'AuraKart';

                  const sellerCity = seller?.city || productData?.sellerCity || productData?.city;
                  const sellerState = seller?.state || productData?.sellerState || productData?.state;

                  if (sellerId) {
                    return (
                      <button
                        onClick={() => {
                          const params = new URLSearchParams({
                            sellerName: sellerName,
                            ...(sellerCity && { city: sellerCity }),
                            ...(sellerState && { state: sellerState }),
                          });
                          navigate(`/seller/${sellerId}?${params.toString()}`);
                        }}
                        className="w-full text-left hover:bg-gray-50 transition-colors rounded p-2 -m-2"
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <Store className="h-5 w-5 text-muted-foreground" />
                            <div>
                              <p className="text-sm text-muted-foreground">Sold by</p>
                              <p className="font-semibold text-gray-900">{sellerName}</p>
                              {(sellerCity || sellerState) && (
                                <p className="text-xs text-muted-foreground mt-1">
                                  {sellerCity}{sellerCity && sellerState && ', '}{sellerState}
                                </p>
                              )}
                            </div>
                          </div>
                          <ChevronRight className="h-4 w-4 text-muted-foreground" />
                        </div>
                      </button>
                    );
                  } else {
                    return (
                      <div className="flex items-center gap-3">
                        <Store className="h-5 w-5 text-muted-foreground" />
                        <div>
                          <p className="text-sm text-muted-foreground">Sold by</p>
                          <p className="font-semibold text-gray-900">{sellerName}</p>
                          {(sellerCity || sellerState) && (
                            <p className="text-xs text-muted-foreground mt-1">
                              {sellerCity}{sellerCity && sellerState && ', '}{sellerState}
                            </p>
                          )}
                        </div>
                      </div>
                    );
                  }
                })()}
              </CardContent>
            </Card>

            {/* Action Buttons */}
            <div ref={actionButtonsRef} className="flex gap-4">
              <Button
                size="lg"
                className="flex-1"
                onClick={handleAddToCart}
                disabled={!displayVariant || displayVariant.stockQuantity === 0}
              >
                Add to Cart
              </Button>
              <Button size="lg" variant="outline" onClick={handleWishlistToggle}>
                <Heart className={cn("h-5 w-5", inWishlist && "fill-red-500 text-red-500")} />
              </Button>
              <Button size="lg" variant="outline">
                <Share2 className="h-5 w-5" />
              </Button>
            </div>

            <Button
              size="lg"
              variant="default"
              className="w-full"
              onClick={handleBuyNow}
              disabled={!displayVariant || displayVariant.stockQuantity === 0}
            >
              Buy Now
            </Button>

            {/* Delivery & Services Section */}
            <div className="space-y-4">
              <h3 className="font-semibold text-lg">Delivery & Services</h3>

              {/* Address Summary */}
              <AddressSummary />

              {/* Delivery Timeline */}
              {displayVariant && product.seller && (
                <DeliveryTimeline
                  seller={product.seller}
                  buyerAddress={
                    (selectedAddress || defaultAddress)
                      ? { city: (selectedAddress || defaultAddress)!.city, state: (selectedAddress || defaultAddress)!.state }
                      : { city: 'Chennai', state: 'Tamil Nadu' }
                  }
                  shipping={{
                    isFree: displayVariant.offerPrice >= 500,
                    cost: displayVariant.offerPrice >= 500 ? 0 : 40
                  }}
                />

              )}

              {/* Return Policy */}
              <div className="flex items-center gap-3 text-sm p-3 bg-blue-50 rounded-lg">
                <Shield className="h-4 w-4 text-blue-600" />
                <div>
                  <p className="font-medium text-blue-900">7 Days Return</p>
                  <p className="text-blue-700">Easy returns and exchanges</p>
                </div>
              </div>
            </div>

            <Accordion type="single" collapsible className="w-full">
              <AccordionItem value="description">
                <AccordionTrigger>Product Details</AccordionTrigger>
                <AccordionContent>
                  <div className="space-y-3 text-sm">
                    <p>{product.productDescription || "No description available."}</p>
                    <div className="grid grid-cols-2 gap-4 mt-4">
                      <div>
                        <span className="font-medium">Brand:</span> {product.details?.brand || "N/A"}
                      </div>
                      <div>
                        <span className="font-medium">Category:</span> {product.categoryName}
                      </div>
                      {product.details?.fabric && (
                        <div>
                          <span className="font-medium">Fabric:</span> {product.details.fabric}
                        </div>
                      )}
                      {product.details?.fit && (
                        <div>
                          <span className="font-medium">Fit:</span> {product.details.fit}
                        </div>
                      )}
                    </div>
                  </div>
                </AccordionContent>
              </AccordionItem>

              <AccordionItem value="care">
                <AccordionTrigger>Care Instructions</AccordionTrigger>
                <AccordionContent>
                  <ul className="space-y-2 text-sm">
                    <li>• Machine wash cold</li>
                    <li>• Do not bleach</li>
                    <li>• Tumble dry low</li>
                    <li>• Iron on low heat</li>
                  </ul>
                </AccordionContent>
              </AccordionItem>
            </Accordion>
          </div>
        </div>

        {/* Similar Products Section */}
        {similarProducts.length > 0 && (
          <div className="mt-12 border-t pt-8">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h2 className="text-2xl font-bold">Similar Products</h2>
                <p className="text-muted-foreground text-sm mt-1">
                  You might also like these
                </p>
              </div>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
              {similarProducts.map((similarProduct) => {
                // Handle image URL - could be string or object
                const productImage = (() => {
                  if (similarProduct.images && similarProduct.images.length > 0) {
                    const firstImage = similarProduct.images[0];
                    // Check if it's an object with image_url property or just a string
                    return typeof firstImage === 'string' ? firstImage : firstImage?.image_url || firstImage;
                  }
                  // Fallback to primaryImageUrl if it exists on the object
                  return (similarProduct as any).primaryImageUrl || null;
                })();

                // Handle pricing - access from variants or direct properties
                const pricing = (() => {
                  if (similarProduct.variants && similarProduct.variants.length > 0) {
                    const firstVariant = similarProduct.variants[0];
                    return {
                      currentPrice: firstVariant.offerPrice || firstVariant.originalPrice || 0,
                      originalPrice: firstVariant.originalPrice || 0,
                      hasDiscount: (firstVariant.discount || 0) > 0
                    };
                  }
                  // Fallback to direct properties if they exist
                  const product = similarProduct as any;
                  return {
                    currentPrice: product.currentPrice || product.price || 0,
                    originalPrice: product.originalPrice || product.price || 0,
                    hasDiscount: (product.discount || 0) > 0
                  };
                })();

                return (
                  <Card key={similarProduct.productId} className="group cursor-pointer hover:shadow-lg transition-shadow">
                    <div
                      onClick={() => navigate(`/product/${similarProduct.productId}`)}
                      className="block"
                    >
                      <div className="aspect-square relative overflow-hidden rounded-t-lg">
                        {productImage ? (
                          <img
                            src={productImage}
                            alt={similarProduct.productName}
                            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-200"
                          />
                        ) : (
                          <div className="w-full h-full bg-gray-100 flex items-center justify-center">
                            <span className="text-gray-400">No Image</span>
                          </div>
                        )}
                      </div>

                      <CardContent className="p-4">
                        <p className="text-sm text-muted-foreground mb-1">
                          {similarProduct.details?.brand || 'AuraKart'}
                        </p>
                        <h3 className="font-medium text-sm line-clamp-2 mb-2">
                          {similarProduct.productName}
                        </h3>

                        {/* Rating */}
                        {(similarProduct.rating || 0) > 0 && (
                          <div className="flex items-center gap-1 mb-2">
                            <Star className="h-3 w-3 fill-yellow-400 text-yellow-400" />
                            <span className="text-xs font-medium">{(similarProduct.rating || 0).toFixed(1)}</span>
                          </div>
                        )}

                        <div className="space-y-1">
                          <div className="flex items-center gap-2">
                            <span className="font-bold">₹{pricing.currentPrice.toFixed(0)}</span>
                            {pricing.hasDiscount && (
                              <span className="text-sm text-muted-foreground line-through">
                                ₹{pricing.originalPrice.toFixed(0)}
                              </span>
                            )}
                          </div>
                          {pricing.hasDiscount && (
                            <Badge className="bg-red-500 hover:bg-red-600 text-xs py-0">
                              {Math.round(((pricing.originalPrice - pricing.currentPrice) / pricing.originalPrice) * 100)}% OFF
                            </Badge>
                          )}
                        </div>
                      </CardContent>
                    </div>
                  </Card>
                );
              })}
            </div>
          </div>
        )}

        {/* Customer Reviews Section */}
        <div id="customer-reviews" className="mt-12 border-t pt-8">
          <h2 className="text-2xl font-bold mb-6">Customer Reviews</h2>

          <div className="grid lg:grid-cols-3 gap-8">
            {/* Rating Summary */}
            <div className="lg:col-span-1">
              <Card className="sticky top-6">
                <CardContent className="p-6">
                  {reviewSummary ? (
                    <>
                      <div className="text-center mb-6">
                        <div className="text-5xl font-bold text-green-600 mb-2">
                          {reviewSummary.averageRating?.toFixed(1) || '0.0'}
                        </div>
                        <div className="flex justify-center gap-1 my-2">
                          {[1, 2, 3, 4, 5].map((star) => (
                            <Star
                              key={star}
                              className={cn(
                                "h-5 w-5",
                                star <= Math.round(reviewSummary.averageRating || 0)
                                  ? "fill-yellow-400 text-yellow-400"
                                  : "text-gray-300"
                              )}
                            />
                          ))}
                        </div>
                        <p className="text-muted-foreground">
                          Based on {reviewSummary.totalReviews || 0} reviews
                        </p>
                      </div>

                      <Separator className="mb-6" />

                      <div className="space-y-2">
                        {(() => {
                          // Check if we have star count data from API
                          const hasStarCounts = reviewSummary.fiveStarCount !== undefined;

                          if (hasStarCounts) {
                            // Use API provided star counts
                            return [
                              { stars: 5, count: reviewSummary.fiveStarCount || 0 },
                              { stars: 4, count: reviewSummary.fourStarCount || 0 },
                              { stars: 3, count: reviewSummary.threeStarCount || 0 },
                              { stars: 2, count: reviewSummary.twoStarCount || 0 },
                              { stars: 1, count: reviewSummary.oneStarCount || 0 },
                            ].map((bar) => {
                              const percentage = reviewSummary.totalReviews > 0
                                ? (bar.count / reviewSummary.totalReviews) * 100
                                : 0;

                              return (
                                <div key={bar.stars} className="flex items-center gap-3">
                                  <span className="text-sm font-medium w-8">{bar.stars}★</span>
                                  <div className="flex-1 bg-gray-200 rounded-full h-2">
                                    <div
                                      className="bg-yellow-400 h-2 rounded-full transition-all duration-300"
                                      style={{ width: `${percentage}%` }}
                                    />
                                  </div>
                                  <span className="text-sm text-muted-foreground w-12 text-right">
                                    {Math.round(percentage)}%
                                  </span>
                                </div>
                              );
                            });
                          } else {
                            // Calculate star distribution from reviews array
                            const starCounts = { 5: 0, 4: 0, 3: 0, 2: 0, 1: 0 };
                            reviews.forEach(review => {
                              if (review.rating >= 1 && review.rating <= 5) {
                                starCounts[Math.floor(review.rating) as keyof typeof starCounts]++;
                              }
                            });

                            return [5, 4, 3, 2, 1].map((star) => {
                              const count = starCounts[star as keyof typeof starCounts];
                              const percentage = reviews.length > 0 ? (count / reviews.length) * 100 : 0;

                              return (
                                <div key={star} className="flex items-center gap-3">
                                  <span className="text-sm font-medium w-8">{star}★</span>
                                  <div className="flex-1 bg-gray-200 rounded-full h-2">
                                    <div
                                      className="bg-yellow-400 h-2 rounded-full transition-all duration-300"
                                      style={{ width: `${percentage}%` }}
                                    />
                                  </div>
                                  <span className="text-sm text-muted-foreground w-12 text-right">
                                    {Math.round(percentage)}%
                                  </span>
                                </div>
                              );
                            });
                          }
                        })()}
                      </div>
                    </>
                  ) : (
                    <div className="text-center py-8">
                      <p className="text-muted-foreground">No reviews yet</p>
                    </div>
                  )}

                  {!userHasReviewed && (
                    <Button
                      className="w-full mt-6"
                      onClick={() => setShowReviewForm(true)}
                    >
                      Write a Review
                    </Button>
                  )}
                </CardContent>
              </Card>
            </div>

            {/* Reviews List */}
            <div className="lg:col-span-2">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-xl font-semibold">
                  Customer Reviews ({reviews.length})
                </h3>
                <Button
                  variant="outline"
                  onClick={() => navigate(`/product/${product.productId}/reviews/${encodeURIComponent(product.productName)}`)}
                >
                  View All Reviews
                </Button>
              </div>

              <div className="space-y-6">
                {reviewLoading ? (
                  <div className="text-center py-12">
                    <Loader2 className="h-8 w-8 animate-spin mx-auto mb-4" />
                    <p className="text-muted-foreground">Loading reviews...</p>
                  </div>
                ) : reviews.length === 0 ? (
                  <Card>
                    <CardContent className="p-12 text-center">
                      <Star className="h-12 w-12 text-gray-300 mx-auto mb-4" />
                      <h3 className="text-xl font-semibold mb-2">No reviews yet</h3>
                      <p className="text-muted-foreground mb-6">
                        Be the first to share your thoughts about this product.
                      </p>
                      {!userHasReviewed && (
                        <Button onClick={() => setShowReviewForm(true)}>
                          Write the first review
                        </Button>
                      )}
                    </CardContent>
                  </Card>
                ) : (
                  reviews.slice(0, 3).map((review) => (
                    <Card key={review.reviewId} className="border border-gray-200">
                      <CardContent className="p-6">
                        {/* Header */}
                        <div className="flex items-center justify-between mb-4">
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 bg-gray-100 rounded-full flex items-center justify-center">
                              <span className="text-sm font-medium text-gray-600">
                                {(() => {
                                  if (!review.userName || review.userName === 'null' || review.userName === 'undefined') {
                                    return 'A';
                                  }
                                  const firstName = review.userName.trim().split(' ')[0];
                                  return (firstName || 'A').charAt(0).toUpperCase();
                                })()}
                              </span>
                            </div>
                            <div>
                              <div className="flex items-center gap-2">
                                <p className="font-semibold text-gray-900">
                                  {(() => {
                                    // Better username handling
                                    if (!review.userName || review.userName === 'null' || review.userName === 'undefined') {
                                      return 'Anonymous';
                                    }
                                    // Extract first name only
                                    const firstName = review.userName.trim().split(' ')[0];
                                    return firstName || 'Anonymous';
                                  })()}
                                </p>
                                {review.isVerifiedPurchase && (
                                  <Badge variant="outline" className="text-xs text-green-600 border-green-600 flex items-center gap-1">
                                    <div className="w-3 h-3 rounded-full bg-green-600 flex items-center justify-center">
                                      <span className="text-white text-xs">✓</span>
                                    </div>
                                    Verified Purchase
                                  </Badge>
                                )}
                              </div>
                            </div>
                          </div>
                          <div className="text-right">
                            <div className="flex items-center gap-1">
                              {[1, 2, 3, 4, 5].map((star) => (
                                <Star
                                  key={star}
                                  className={cn(
                                    "h-4 w-4",
                                    star <= review.rating
                                      ? "fill-yellow-400 text-yellow-400"
                                      : "text-gray-300"
                                  )}
                                />
                              ))}
                            </div>
                            <p className="text-sm text-muted-foreground mt-1">
                              {new Date(review.createdDate).toLocaleDateString('en-US', {
                                year: 'numeric',
                                month: 'long',
                                day: 'numeric'
                              })}
                            </p>
                          </div>
                        </div>

                        {/* Review Title */}
                        {review.reviewTitle && (
                          <h3 className="font-semibold text-gray-900 mb-2">
                            {review.reviewTitle}
                          </h3>
                        )}

                        {/* Review Comment */}
                        {review.comment && (
                          <p className="text-gray-700 mb-4 leading-relaxed">
                            {review.comment}
                          </p>
                        )}

                        {/* Review Images */}
                        {review.imageUrls && review.imageUrls.length > 0 && (
                          <div className="mb-4">
                            <div className="flex gap-2 overflow-x-auto pb-2">
                              {review.imageUrls.slice(0, 4).map((url, idx) => (
                                <div
                                  key={`${review.reviewId}-${idx}`}
                                  className="flex-shrink-0 w-20 h-20 rounded-lg overflow-hidden border border-gray-200 hover:border-gray-400 transition-colors cursor-pointer hover:shadow-md"
                                  onClick={() => setSelectedReviewImage(url)}
                                >
                                  <img
                                    src={url}
                                    alt={`Review image ${idx + 1}`}
                                    className="w-full h-full object-cover hover:scale-105 transition-transform duration-200"
                                    onError={(e) => {
                                      const target = e.target as HTMLImageElement;
                                      target.style.display = 'none';
                                    }}
                                  />
                                </div>
                              ))}
                              {review.imageUrls.length > 4 && (
                                <div
                                  className="flex-shrink-0 w-20 h-20 rounded-lg border border-gray-200 flex items-center justify-center bg-gray-50 hover:bg-gray-100 transition-colors cursor-pointer"
                                  onClick={() => setSelectedReviewImage(review.imageUrls[4])}
                                >
                                  <span className="text-xs text-gray-500 font-medium">
                                    +{review.imageUrls.length - 4}
                                  </span>
                                </div>
                              )}
                            </div>
                          </div>
                        )}

                        {/* Helpful Button */}
                        <div className="flex items-center justify-between">
                          <Button
                            variant="ghost"
                            size="sm"
                            className="text-muted-foreground hover:text-gray-700"
                            onClick={() => {
                              // Add helpful functionality if needed
                            }}
                          >
                            👍 Helpful ({review.helpfulCount || 0})
                          </Button>
                        </div>
                      </CardContent>
                    </Card>
                  ))
                )}

                {reviews.length > 3 && (
                  <div className="text-center pt-4">
                    <Button
                      variant="outline"
                      onClick={() => navigate(`/product/${product.productId}/reviews/${encodeURIComponent(product.productName)}`)}
                    >
                      View All {reviews.length} Reviews
                    </Button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Review Form Modal */}
        {showReviewForm && product && (
          <ReviewFormModal
            isOpen={showReviewForm}
            onClose={() => setShowReviewForm(false)}
            productId={product.productId}
            productName={product.productName}
          />
        )}

        {/* Review Image Modal */}
        {selectedReviewImage && (
          <div
            className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4"
            onClick={() => setSelectedReviewImage(null)}
          >
            <div className="relative max-w-4xl max-h-full">
              <button
                onClick={() => setSelectedReviewImage(null)}
                className="absolute top-4 right-4 text-white bg-black bg-opacity-50 rounded-full w-10 h-10 flex items-center justify-center hover:bg-opacity-70 transition-colors z-10"
              >
                ✕
              </button>
              <img
                src={selectedReviewImage}
                alt="Review image full size"
                className="max-w-full max-h-full object-contain rounded-lg"
                onClick={(e) => e.stopPropagation()}
              />
            </div>
          </div>
        )}

        {/* Size Chart Modal */}
        <Dialog open={showSizeChart} onOpenChange={setShowSizeChart}>
          <DialogContent className="max-w-4xl max-h-[90vh] overflow-hidden">
            <DialogHeader>
              <DialogTitle>Size Chart</DialogTitle>
            </DialogHeader>
            <div className="flex justify-center items-center p-4 max-h-[calc(90vh-100px)] overflow-auto">
              {sizeChartImage ? (
                <img
                  src={sizeChartImage}
                  alt="Size Chart"
                  className="max-w-full max-h-full object-contain rounded-lg"
                  style={{ width: 'auto', height: 'auto' }}
                  onError={(e) => {
                    const target = e.currentTarget as HTMLImageElement;
                    target.style.display = 'none';
                    const parent = target.parentElement;
                    if (parent) {
                      parent.innerHTML = '<p class="text-red-500 text-center">Failed to load size chart</p>';
                    }
                  }}
                />
              ) : (
                <div className="text-center p-8">
                  <p className="text-muted-foreground text-lg">Size chart not available</p>
                  <p className="text-sm text-muted-foreground mt-2">Contact customer support for sizing guidance</p>
                </div>
              )}
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
};

export default ProductDetailPage;